import { Box, Flex, Spinner, Text } from "@chakra-ui/react";
import { documentItemApi } from "apiClient/v2";
import { DOCUMENT_TYPE_INFO } from "constants/document";
import { TypeFieldDynamic } from "constants/enum";
import { useAuthorization } from "hooks/usePermission";
import useUserOfProject from "hooks/useUserOfProject";
import { DocumentCategoryDTO } from "interfaces/dtos/documentCategoryDTO";
import { DocumentItemDTO } from "interfaces/dtos/documentItemDTO";
import { DataLog } from "interfaces/models/dataLog";
import { DocumentTemplate } from "interfaces/models/documentTemplate";
import { GetContentLog, insertDataLogToIndexedDb } from "models/dataLog";
import { getColorTextByStatus } from "models/document";
import { useForgeViewerContext } from "pages/forge-viewer/ForgeViewerContext";
import { TypeHandleInitData } from "pages/forge-viewer/hooks/useSupportSyncDataOffline";
import { memo, useCallback } from "react";
import { useDispatch } from "react-redux";
import { updateDocumentItem } from "redux/documentSlice";
import { sortArrayByField } from "utils/array";
import { transformBodyForCombineData } from "utils/offline";
import DynamicFields from "../DocumentCategoryModal/DynamicFields";
import FieldNoComponent from "./FieldNoComponent";
import FormItemForDocumentData from "./FormItemForDocumentData";
import { useItemLogs } from "./hooks";
import useListenFromExternalLog from "./hooks/useListenFromExternalLog";
import useRevertDataByLogs from "./hooks/useRevertDataByLogs";
import ItemLog from "./ItemLog";
import ItemStatusMenu from "./ItemStatusMenu";
import ItemTasks from "./ItemTasks";
import ItemTitle, { ItemTitleType } from "./ItemTitle";

interface Props {
  isLoadedViewer: boolean;
  documentItemSelected?: DocumentItemDTO;
  documentCategorySelected?: DocumentCategoryDTO;
  documentTemplate?: DocumentTemplate;
  itemStatusColor: string;
  isOnline: boolean;
  itemDisplayOrder: number | string;
  dataLogTemp: DataLog | undefined;
  fromExternalLogs: DataLog[] | undefined;

  clearFromExternalLogs: () => void;
}

const FormItemPAC = (props: Props) => {
  const {
    isLoadedViewer,
    documentItemSelected,
    documentCategorySelected,
    documentTemplate,
    itemStatusColor,
    isOnline,
    itemDisplayOrder,
    dataLogTemp,
    fromExternalLogs,
    clearFromExternalLogs,
  } = props;
  const dispatch = useDispatch();
  const { currentUser, listUserById, listAllUserById } = useUserOfProject();
  const { itemLogs, isLoadMoreLogs, mapDynamicFieldTitle, setItemLogs } =
    useItemLogs({
      itemId: documentItemSelected?.id,
      documentTemplate,
      dataLogTemp,
    });
  const { socket } = useForgeViewerContext();
  const { canEditDocumentItem } = useAuthorization();

  useListenFromExternalLog({
    fromExternalLogs,
    setItemLogs,
    clearFromExternalLogs,
  });

  const insertItemLog = useCallback(
    async (params: GetContentLog) => {
      if (!currentUser?.id || !documentItemSelected?.id) {
        return;
      }

      const log = await insertDataLogToIndexedDb({
        contentLogParams: params,
        createdBy: currentUser.id,
        identifyValue: documentItemSelected.id,
        identifyCol: "itemId",
        type: "item",
      });

      socket.addDocItemLog(documentItemSelected.id, log);

      setItemLogs((prev) =>
        sortArrayByField<DataLog>([log, ...prev], "createdAt", false)
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [documentItemSelected?.id, currentUser?.id, documentCategorySelected?.id]
  );

  const { onRevertDataByLog } = useRevertDataByLogs({
    insertLog: insertItemLog,
    documentItemSelected,
  });

  const handleSaveDynamicField = useCallback(
    async (item: DocumentItemDTO) => {
      if (!documentItemSelected?.id) {
        return;
      }
      const now = new Date();
      documentItemApi
        .updateItem(
          transformBodyForCombineData<DocumentItemDTO>({
            body: {
              id: documentItemSelected?.id,
              data: item.data,
              mapTitleKey: item.mapTitleKey,
              mapDisplayValueKey: item.mapDisplayValueKey,
              mapDisplayLabelKey: item.mapDisplayLabelKey,
              mapDisplayValueTypeKey: item.mapDisplayValueTypeKey,
              updatedAt: now,
            } as DocumentItemDTO,
            bodyBefore: documentItemSelected,
            typeInitData: TypeHandleInitData.DOCUMENT_ITEM,
          })
        )
        .then(({ data }) => {
          socket.updateDocItem(documentItemSelected, {
            data: item.data,
            updatedAt: data.updatedAt,
          });
        });

      dispatch(
        updateDocumentItem({
          ...documentItemSelected,
          data: { ...(documentItemSelected?.data || {}), ...item.data },
          updatedAt: now,
        })
      );
    },
    [documentItemSelected, dispatch, socket]
  );

  return (
    <Flex flexDirection="column" padding="0px 1.6rem 0" minHeight="100%">
      <Flex gap="1.2rem" mb="3.2rem" alignItems="center">
        <FieldNoComponent
          color={itemStatusColor}
          colorText={getColorTextByStatus(documentItemSelected?.status)}
          no={itemDisplayOrder}
        />

        <Flex flexDirection="column" flex={1}>
          <Text lineHeight="2.2rem" color="#737373" fontSize="1.4rem">
            {
              //@ts-ignore
              DOCUMENT_TYPE_INFO[documentItemSelected?.documentType || ""]
                ?.typeName || ""
            }
          </Text>
          <ItemTitle
            type={ItemTitleType.ITEM}
            item={documentItemSelected}
            inputProps={{ isDisabled: !canEditDocumentItem }}
            insertItemLog={insertItemLog}
          />
        </Flex>
      </Flex>

      {/* document data field */}
      <FormItemForDocumentData
        mb="3.2rem"
        template={documentTemplate}
        item={documentItemSelected}
        isDisabled={!canEditDocumentItem}
        insertItemLog={insertItemLog}
      />

      {/* dynamic field */}
      <DynamicFields
        type={TypeFieldDynamic.TYPE_ITEM}
        template={documentTemplate}
        documentItemSelected={documentItemSelected}
        insertDocumentItemLog={insertItemLog}
        onSaveDataForDocumentItem={handleSaveDynamicField}
        isDisabled={!canEditDocumentItem}
        mb="3.2rem"
      />

      {/* insert task */}
      <Box mb="3.2rem" borderTop="1px solid #A3A3A3" pt="2rem">
        <ItemTasks
          isOnline={isOnline}
          documentItem={documentItemSelected!}
          isDisabled={!canEditDocumentItem}
          insertDocumentItemLog={insertItemLog}
        />
      </Box>

      {/* logs */}
      <Box
        backgroundColor="#E8E8E8"
        ml="-1.6rem"
        mt="auto"
        width="calc(100% + 3.2rem)"
        padding="2rem 1.6rem"
      >
        <Text
          display="block"
          fontSize="1.6rem"
          color="var(--primary-text-color)"
          fontWeight="bold"
          mb="1.2rem"
        >
          変更履歴
        </Text>
        {itemLogs?.map((item) => (
          <ItemLog
            key={item.id}
            log={item}
            user={listAllUserById?.[item?.createdBy || ""]}
            currentUser={currentUser}
            listUserById={listUserById}
            mapDynamicFieldTitle={mapDynamicFieldTitle}
            documentItem={documentItemSelected}
            onRevertDataByLog={onRevertDataByLog}
          />
        ))}

        {isLoadMoreLogs && (
          <Flex height="4rem" justifyContent="center" alignItems="center">
            <Spinner size="md" color="blue.500" />
          </Flex>
        )}
      </Box>

      {/* status */}
      <Flex
        position="sticky"
        width="calc(100% + 3.2rem)"
        marginLeft="-1.6rem"
        bottom="0"
        left="0"
        height="5.8rem"
        zIndex="12"
        backgroundColor="#E8E8E8"
        borderTop="1px solid rgba(0,0,0,0.15)"
        justifyContent="space-between"
        padding="0px 1.6rem"
        alignItems="center"
        gap="0.8rem"
      >
        <Text
          flexShrink={0}
          color="var(--primary-text-color)"
          fontSize="1.4rem"
        >
          ステータス
        </Text>
        <Box flex={1} maxW="35rem">
          <ItemStatusMenu
            isLoadedViewer={isLoadedViewer}
            currentUser={currentUser}
            documentItemSelected={documentItemSelected}
            documentCategorySelected={documentCategorySelected}
            isDisabled={!canEditDocumentItem}
            insertDocumentItemLog={insertItemLog}
          />
        </Box>
      </Flex>
    </Flex>
  );
};

export default memo(FormItemPAC);
